<template>
  <div class="wrapper">

    <!-- env + maintenance banners -->
    <EnvNotifier :env="ENV.ENV" />
    <MaintenanceNotifier
      v-if="maintenance.start && maintenance.end"
      :enabled="maintenance.enabled"
      :domain="maintenance.domain"
      :start="maintenance.start"
      :end="maintenance.end"
      :message="maintenance.message"
    />

    <!-- (Optional) promo bar you commented in Blade; keep as a component if you need it -->
    <!-- <PromoBar /> -->

    <!-- Carry blade-app attributes: we can read token from route/query on mount -->
    <div id="blade-app" class="wrapper__content changirecommends" :class="{ 'mt-4': ENV.ENV != 'production' }">
      <ChangiRecommendsHeader :isHomePage="isHomePage" />

      <!-- global modals / utilities -->
      <AlcoholAlert :image="settings.get_it_alcohol_modal_image" />

      <!-- page content -->
      <main id="main-content">
        <router-view :key="$route.fullPath.split('#')[0]" />
      </main>

      <!-- global svg sprite + footer -->
      <SvgSprite />
      <CrFooter
        v-if="!isMaintenancePage"
        :mailchimp-audience-id="settings.changirecommends_mailchimp_audience_id"
        :mailchimp-user-id="settings.changirecommends_mailchimp_user_id"
        footer-top-menu-slug="get-it-footer-menu"
      />
    </div>
  </div>
</template>

<script>
// Vue 2 – classless options API
import EnvNotifier from '@/components/EnvNotifier.vue'            // port of @include('env-notifier')
import MaintenanceNotifier from '@/components/MaintenanceNotifier.vue' // port of @include('maintenance-notifier')
import AlcoholAlert from '@/components/AlcoholAlert.vue'
import SvgSprite from '@/components/SvgSprite.vue'
import CrFooter from '@/components/CrFooter.vue'
import ChangiRecommendsHeader from '@/modules/header/containers/Header.vue'
import { handleInternalLink } from '@/router/routerUtils'
import { mapActions } from 'vuex'
import { ENV } from '@/env'

export default {
  name: 'App',
  components: {
    EnvNotifier,
    MaintenanceNotifier,
    AlcoholAlert,
    SvgSprite,
    CrFooter,
    ChangiRecommendsHeader
  },
  data () {
    return {
      settings: {
        // You probably fetch these from your backend's settings endpoint
        get_it_alcohol_modal_image: null,
        changirecommends_mailchimp_audience_id: null,
        changirecommends_mailchimp_user_id: null
      },
      // Map these from your backend settings or env
      maintenance: {
        enabled: false,               // will be fetched from API
        domain: false,                // will be fetched from API
        start: '',                    // will be fetched from API
        end: '',                      // will be fetched from API
        message: ''                   // will be fetched from API
      }
    }
  },
  computed: {
    ENV() {
      return ENV
    },
    isMaintenancePage() {
      return this.$route.name === 'maintenance'
    },
    isHomePage() {
      return this.$route.name === 'changi_recommends.index'
    }
  },
  watch: {
    '$route.query.source': {
      immediate: true,
      handler (source) {
        if (source === 'simplygo') {
          localStorage.setItem('simple_layout', '1')
        } else if (source === 'reset') {
          localStorage.removeItem('simple_layout')
        }

        // Always hydrate as boolean
        this.$set(this.$root, 'simple_layout', localStorage.getItem('simple_layout') === '1')
      }
    }
  },
  created () {
    // Make maintenance data globally accessible
    this.$root.maintenance = this.maintenance
    
    // Example: pass token from ?setToken=... (Blade added token attr; SPA uses query)
    const setToken = this.$route.query.setToken
    if (setToken) {
      this.applyToken(String(setToken))
      // remove it from the URL without reloading
      this.$router.replace({ query: { ...this.$route.query, setToken: undefined } })
    }
    
    // Load maintenance data from API
    // this.fetchMaintenanceData()
      
    // Set up periodic maintenance check (every 5 minutes)
    // this.maintenanceInterval = setInterval(() => {
    //   this.fetchMaintenanceData()
    // }, 1 * 60 * 1000)
  },
  mounted() {
    document.addEventListener('click', handleInternalLink)
  },
  beforeDestroy() {
    document.removeEventListener('click', handleInternalLink)
    // Clean up interval
    if (this.maintenanceInterval) {
      clearInterval(this.maintenanceInterval)
    }
  },
  methods: {
    ...mapActions('auth', ['applyToken']), // implement in your store if relevant
    async fetchMaintenanceData () {
      try {
        const response = await fetch(`${ENV.CORE_URL}/api/v1/maintenance/check`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`Maintenance API request failed: ${response.status}`)
        }
        
        const data = await response.json()
        
        // Map API response to maintenance object
        this.maintenance = {
          enabled: data.maintenanceNotifierEnabled === '1',
          domain: data.maintenanceNotifierDomain,
          start: data.maintenanceNotifierStartDate,
          end: data.maintenanceNotifierEndDate,
          message: data.maintenanceNotifierMessage || ''
        }
        
        // Update global reference
        this.$root.maintenance = this.maintenance
        
        // Store in sessionStorage for router access
        sessionStorage.setItem('maintenance_data', JSON.stringify(this.maintenance))
        
        // Check if we need to redirect to maintenance page
        this.checkMaintenanceRedirect()
      } catch (error) {
        console.error('Failed to fetch maintenance data:', error)
        // Keep default values (maintenance disabled) on error
        this.maintenance = {
          enabled: false,
          domain: false,
          start: '',
          end: '',
          message: ''
        }
        this.$root.maintenance = this.maintenance
        sessionStorage.setItem('maintenance_data', JSON.stringify(this.maintenance))
      }
    },
    checkMaintenanceRedirect() {
      if (this.maintenance.enabled && this.maintenance.start && this.maintenance.end) {
        const now = new Date()
        const start = new Date(this.maintenance.start)
        const end = new Date(this.maintenance.end)
        
        if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && now >= start && now <= end) {
          // Only redirect if not already on maintenance page
          if (this.$route.name !== 'maintenance') {
            this.$router.push({ name: 'maintenance' })
          }
        }
      }
    },
    testMaintenanceMode() {
      console.log('Manually triggering maintenance mode...')
      this.maintenance.enabled = true
      this.maintenance.domain = true
      this.maintenance.start = new Date().toISOString() // Set to now
      this.maintenance.end = new Date(Date.now() + 30 * 60 * 1000).toISOString() // Set to 30 minutes from now
      this.maintenance.message = 'This is a test maintenance message.'
      this.$root.maintenance = this.maintenance
      sessionStorage.setItem('maintenance_data', JSON.stringify(this.maintenance))
      this.checkMaintenanceRedirect()
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/app.scss";
/* Or import SCSS via main.js if you prefer */

.wrapper { min-height: 100vh; display: flex; flex-direction: column; }
.wrapper__content { flex: 1 0 auto; }
</style>